import React, { useCallback, useEffect, useState } from 'react';
import { ReactFlow, Background, Controls, MiniMap, useNodesState, useEdgesState, addEdge, Connection, Edge, EdgeChange } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CustomNode from './CustomNode';
import CustomEdge from './CustomEdge';
const initialNodes = [{
  id: '1',
  type: 'custom',
  position: {
    x: 500,
    y: 50
  },
  data: {
    label: 'Extracting structure from unstructured data:\nPipeline vs RAG vs ChatGPT',
    pros: [],
    cons: []
  }
}, {
  id: '2',
  type: 'custom',
  position: {
    x: 800,
    y: 300
  },
  data: {
    label: 'RAG - especially with vector similarity',
    pros: ['Works at very large scale', 'Fast, particularly well suited for ad hoc requests or search', 'Included in many data warehousing platforms'],
    cons: ['Results need stitching together', 'Challenges with vector similarity', 'Niche, domain-specific materials are "similar" -> embeddings get clustered close together', 'No guarantees of coverage (if approximate answers are acceptable, this is acceptable)', 'Trade-off in choosing number of retrieved results: too few -> false negatives, too many -> context gets overloaded (see context rot)', 'Complexity in optimizing: chunking, reranking, hybrid search -> requires significant engineering']
  }
}, {
  id: '3',
  type: 'custom',
  position: {
    x: 1300,
    y: 300
  },
  data: {
    label: 'ChatGPT / Claude / Copilot',
    pros: ['Extremely easy to try', 'Business user friendly simple UI', 'Can easily blend in online data', 'Ad-hoc questions and modifications'],
    cons: ['Endless prompt engineering and back & forths', 'Limited file input formats and scale', 'Unsuitable for repeating workflows', 'No dedicated data first UI -> wall of text output', 'Unreliability in accuracy and formats', 'References and traceability are painful', 'Structured data is one-off file download']
  }
}, {
  id: '4',
  type: 'custom',
  position: {
    x: 100,
    y: 300
  },
  data: {
    label: 'SOTA model (e.g. GPT5.2-Pro) with entire corpus',
    pros: ['Fairly "intelligent"', 'Works with basic prompts', 'Works on small, well-defined datasets'],
    cons: ['Hits context limits quickly', 'Long, hard-to-read outputs', 'Poor citation usability']
  }
}, {
  id: '5',
  type: 'custom',
  position: {
    x: 100,
    y: 600
  },
  data: {
    label: 'SOTA large context model (e.g. Gemini 3-Pro)',
    pros: ['Much larger context window', 'Pros from above still apply'],
    cons: ['Suffers from context-rot', 'Output length limitation issues', 'Tends to skip steps or return incomplete results (laziness)', 'High probability of mistakes']
  }
}, {
  id: '6',
  type: 'custom',
  position: {
    x: 100,
    y: 900
  },
  data: {
    label: 'Document-level processing',
    pros: ['Mostly solves context window constraints', 'More reliability and accuracy'],
    cons: ['Output limits are still a limitation', 'Requires combining results from different documents', 'Requires reconciliation of layouts and definitions across documents']
  }
}, {
  id: '7',
  type: 'custom',
  position: {
    x: 100,
    y: 1200
  },
  data: {
    label: 'Page-level processing',
    pros: ['Improves reliability of extraction', 'Context window problem solved for both input and output'],
    cons: ['Mistakes still occur on complex documents (Pure text extraction companies exist for a reason)', 'Loses context between pages', 'Still requires stitching and reconciliation even within one document']
  }
}, {
  id: '8',
  type: 'custom',
  position: {
    x: 100,
    y: 1500
  },
  data: {
    label: 'Traditional tools i.e. XYZ Document Intelligence',
    pros: ['Proprietary models can be faster than general-purpose LLMs', 'Good integrations', 'Sourcing within pages are reliable'],
    cons: ['Lower quality output for non-mechanical textual extractions', 'Requires heavy IT setup and integration', 'Requires reconciliation across pages and documents']
  }
}, {
  id: '9',
  type: 'custom',
  position: {
    x: 500,
    y: 1800
  },
  data: {
    label: 'Parsewise',
    pros: ['Reliability', 'Accuracy', 'Reconciliation across your corpus out of the box'],
    cons: ['Less playing around with raw model APIs']
  }
}];
const initialEdges = [{
  id: 'e1-2',
  source: '1',
  target: '2',
  type: 'custom',
  data: {
    label: 'RAG'
  }
}, {
  id: 'e1-3',
  source: '1',
  target: '3',
  type: 'custom',
  data: {
    label: 'ChatGPT'
  }
}, {
  id: 'e1-4',
  source: '1',
  target: '4',
  type: 'custom',
  data: {
    label: 'Pipeline'
  }
}, {
  id: 'e4-5',
  source: '4',
  target: '5',
  type: 'custom',
  data: {
    label: "Let's increase the context window"
  }
}, {
  id: 'e5-6',
  source: '5',
  target: '6',
  type: 'custom',
  data: {
    label: "Instead of increasing the context window, let's split up the content"
  }
}, {
  id: 'e6-7',
  source: '6',
  target: '7',
  type: 'custom',
  data: {
    label: "Let's split up the content even further..."
  }
}, {
  id: 'e7-8',
  source: '7',
  target: '8',
  type: 'custom',
  data: {
    label: 'Maybe we get more reliability from traditional tools?'
  }
}, {
  id: 'e8-9',
  source: '8',
  target: '9',
  type: 'custom',
  data: {
    label: 'Let us ensure maximum accuracy'
  }
}, {
  id: 'e2-9',
  source: '2',
  target: '9',
  type: 'custom',
  data: {
    label: 'Let us ensure maximum accuracy'
  }
}, {
  id: 'e3-9',
  source: '3',
  target: '9',
  type: 'custom',
  data: {
    label: 'Let us set up a scalable workflow'
  }
}];
const nodeTypes = {
  custom: CustomNode
};
const edgeTypes = {
  custom: CustomEdge
};
const DecisionTree = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const onConnect = useCallback((connection: Connection) => {
    const newEdge = {
      ...connection,
      type: 'custom',
      data: {
        label: 'New connection'
      }
    };
    setEdges(eds => addEdge(newEdge, eds));
  }, [setEdges]);
  const handleEdgeLabelChange = useCallback((edgeId: string, newLabel: string) => {
    setEdges(eds => eds.map(edge => {
      if (edge.id === edgeId) {
        return {
          ...edge,
          data: {
            ...edge.data,
            label: newLabel
          }
        };
      }
      return edge;
    }));
  }, [setEdges]);
  const onEdgesChangeWithLabelUpdate = useCallback((changes: EdgeChange[]) => {
    onEdgesChange(changes);
    // Add label change handler to all edges
    setEdges(currentEdges => currentEdges.map(edge => ({
      ...edge,
      data: {
        ...edge.data,
        onLabelChange: handleEdgeLabelChange
      }
    })));
  }, [onEdgesChange, handleEdgeLabelChange, setEdges]);
  // Initialize edges with the label change handler
  useEffect(() => {
    setEdges(currentEdges => currentEdges.map(edge => ({
      ...edge,
      data: {
        ...edge.data,
        onLabelChange: handleEdgeLabelChange
      }
    })));
  }, [setEdges, handleEdgeLabelChange]);
  return <div className="w-full h-full" data-id="element-21">
      <ReactFlow nodes={nodes} edges={edges} onNodesChange={onNodesChange} onEdgesChange={onEdgesChangeWithLabelUpdate} onConnect={onConnect} nodeTypes={nodeTypes} edgeTypes={edgeTypes} fitView fitViewOptions={{
      padding: 0.3,
      includeHiddenNodes: false,
      nodes: ['1', '2', '3', '4'] // Only focus on these nodes
    }} nodesDraggable={true} minZoom={0.2} maxZoom={2} defaultViewport={{
      x: 0,
      y: 0,
      zoom: 0.8
    }} data-id="element-22">
        <Background data-id="element-23" />
        <Controls data-id="element-24" />
        <MiniMap data-id="element-25" />
      </ReactFlow>
    </div>;
};
export default DecisionTree;